import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Send, Plus, Search, Filter, Calendar, Mail, Phone, ExternalLink,
  Edit2, Trash2, Eye, RefreshCw, DollarSign, CheckCircle, Clock,
  FileText, MoreHorizontal
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from "../components/ui/dropdown-menu";
import { usePermissions } from "../contexts/PermissionsContext";

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-slate-500 text-white", icon: FileText },
  sent: { label: "Sent", color: "bg-blue-500 text-white", icon: Send },
  viewed: { label: "Viewed", color: "bg-blue-600 text-white", icon: Eye },
  signed: { label: "Signed", color: "bg-emerald-500 text-white", icon: CheckCircle },
  payment_pending: { label: "Payment Pending", color: "bg-amber-500 text-white", icon: Clock },
  deposit_paid: { label: "Deposit Paid", color: "bg-emerald-600 text-white", icon: DollarSign },
  paid_in_full: { label: "Paid in Full", color: "bg-emerald-700 text-white", icon: CheckCircle },
  expired: { label: "Expired", color: "bg-red-500 text-white", icon: Clock },
  cancelled: { label: "Cancelled", color: "bg-red-600 text-white", icon: Trash2 }
};

// StatCard component defined outside ProposalsPage to prevent re-creation on each render
const StatCard = ({ title, value, icon: Icon, color }) => (
  <Card className="bg-slate-800">
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const ProposalsPage = () => {
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [stats, setStats] = useState({
    total: 0,
    draft: 0,
    sent: 0,
    signed: 0,
    paid: 0
  });

  const fetchProposals = useCallback(async () => {
    setLoading(true);
    try {
      let url = "/proposals";
      if (statusFilter !== "all") {
        url += `?status=${statusFilter}`;
      }
      const res = await API.get(url);
      // Handle both old format (array) and new format ({ proposals: [], stats: {} })
      const proposalsData = Array.isArray(res.data) ? res.data : (res.data.proposals || []);
      setProposals(proposalsData);
      
      // Use stats from API if available, otherwise calculate locally
      if (res.data.stats) {
        setStats(res.data.stats);
      } else {
        // Calculate stats for backward compatibility
        const allProposals = statusFilter === "all" ? proposalsData : await API.get("/proposals").then(r => Array.isArray(r.data) ? r.data : (r.data.proposals || []));
        setStats({
          total: allProposals.length,
          draft: allProposals.filter(p => p.status === "draft").length,
          sent: allProposals.filter(p => ["sent", "viewed"].includes(p.status)).length,
          signed: allProposals.filter(p => ["signed", "payment_pending"].includes(p.status)).length,
          paid: allProposals.filter(p => ["deposit_paid", "paid_in_full"].includes(p.status)).length
        });
      }
    } catch (error) {
      toast.error("Failed to load proposals");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  const handleDelete = async (proposalId) => {
    if (!confirm("Are you sure you want to delete this proposal?")) return;
    try {
      await API.delete(`/proposals/${proposalId}`);
      toast.success("Proposal deleted");
      fetchProposals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete proposal");
    }
  };

  const handleSend = async (proposalId) => {
    try {
      await API.post(`/proposals/${proposalId}/send`);
      toast.success("Proposal sent to client via email and SMS!");
      fetchProposals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send proposal");
    }
  };

  const handleRemind = async (proposalId) => {
    try {
      await API.post(`/proposals/${proposalId}/remind`);
      toast.success("Reminder sent to client!");
      fetchProposals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminder");
    }
  };

  const filteredProposals = proposals.filter(p => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      p.title?.toLowerCase().includes(search) ||
      p.client_name?.toLowerCase().includes(search) ||
      p.client_email?.toLowerCase().includes(search) ||
      p.estimate_number?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-6 animate-slide-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-chivo font-bold text-white tracking-tight">Proposals</h1>
          <p className="text-slate-400 font-ibm mt-1">Manage client proposals and track approvals</p>
        </div>
        {can('create_proposal') && (
          <Button onClick={() => navigate("/estimating")} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold" data-testid="new-proposal-btn">
            <Plus className="h-4 w-4 mr-2" /> New Proposal
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard title="Total Proposals" value={stats.total} icon={FileText} color="bg-slate-700" />
        <StatCard title="Drafts" value={stats.draft} icon={Edit2} color="bg-slate-500" />
        <StatCard title="Sent/Viewed" value={stats.sent} icon={Send} color="bg-blue-500" />
        <StatCard title="Signed" value={stats.signed} icon={CheckCircle} color="bg-emerald-500" />
        <StatCard title="Paid" value={stats.paid} icon={DollarSign} color="bg-emerald-700" />
      </div>

      {/* Filters */}
      <Card className="bg-slate-900 border-slate-700">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by title, client, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="viewed">Viewed</SelectItem>
                <SelectItem value="signed">Signed</SelectItem>
                <SelectItem value="payment_pending">Payment Pending</SelectItem>
                <SelectItem value="deposit_paid">Deposit Paid</SelectItem>
                <SelectItem value="paid_in_full">Paid in Full</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchProposals}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Proposals List */}
      <Card className="bg-slate-900 border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg text-white">
            {filteredProposals.length} Proposal{filteredProposals.length !== 1 ? 's' : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : filteredProposals.length === 0 ? (
            <div className="text-center py-12">
              <Send className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No proposals found</h3>
              <p className="text-slate-500 mb-4">
                {searchTerm || statusFilter !== "all" 
                  ? "Try adjusting your filters" 
                  : "Create your first proposal from an estimate"}
              </p>
              <Button onClick={() => navigate("/estimating")}>
                Go to Estimating
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredProposals.map((proposal) => {
                const statusConfig = STATUS_CONFIG[proposal.status] || STATUS_CONFIG.draft;
                const StatusIcon = statusConfig.icon;
                
                return (
                  <div
                    key={proposal.proposal_id}
                    className="flex items-center justify-between p-4 border border-slate-700 rounded-lg hover:border-slate-500 hover:bg-slate-800/50 transition-all bg-slate-900"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-white truncate">
                          {proposal.title || proposal.project_name || "Untitled Proposal"}
                        </h3>
                        <Badge className={`${statusConfig.color} flex items-center gap-1`}>
                          <StatusIcon className="h-3 w-3" />
                          {statusConfig.label}
                        </Badge>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-400">
                        <span className="font-mono text-slate-300">{proposal.estimate_number}</span>
                        
                        {proposal.client_name && (
                          <span className="flex items-center gap-1">
                            <span className="font-medium text-slate-300">{proposal.client_name}</span>
                          </span>
                        )}
                        
                        {proposal.client_email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {proposal.client_email}
                          </span>
                        )}
                        
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(proposal.created_at).toLocaleDateString()}
                        </span>
                        
                        {proposal.sent_at && (
                          <span className="text-blue-400">
                            Sent {new Date(proposal.sent_at).toLocaleDateString()}
                          </span>
                        )}
                        
                        {proposal.signed_at && (
                          <span className="text-emerald-400">
                            Signed {new Date(proposal.signed_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      {proposal.status !== "draft" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(`/proposal/${proposal.public_token}`, '_blank')}
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      )}
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/proposal-builder/${proposal.proposal_id}`)}
                      >
                        <Edit2 className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/proposal-builder/${proposal.proposal_id}`)}>
                            <Edit2 className="h-4 w-4 mr-2" />
                            Edit Proposal
                          </DropdownMenuItem>
                          {proposal.status === "draft" && (
                            <DropdownMenuItem onClick={() => handleSend(proposal.proposal_id)}>
                              <Send className="h-4 w-4 mr-2" />
                              Send to Client
                            </DropdownMenuItem>
                          )}
                          {["sent", "viewed"].includes(proposal.status) && (
                            <DropdownMenuItem onClick={() => handleRemind(proposal.proposal_id)}>
                              <Mail className="h-4 w-4 mr-2" />
                              Send Reminder
                            </DropdownMenuItem>
                          )}
                          {proposal.status !== "draft" && (
                            <DropdownMenuItem onClick={() => {
                              const link = proposal.unique_token 
                                ? `${window.location.origin}/view-proposal/${proposal.unique_token}`
                                : `${window.location.origin}/proposal/${proposal.public_token}`;
                              navigator.clipboard.writeText(link);
                              toast.success("Link copied to clipboard");
                            }}>
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Copy Public Link
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDelete(proposal.proposal_id)}
                            className="text-red-600"
                            disabled={["signed", "deposit_paid", "paid_in_full"].includes(proposal.status)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProposalsPage;
